import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';

interface ParkingSpace {
  id: string;
  spaceNumber: string;
  status: string;
  currentUser?: {
    name: string;
    phoneNumber: string;
    organization: string;
  };
  usagePeriod?: {
    startDate: any;
    endDate: any;
  };
}

interface ParkingApplication {
  id: string;
  uid: string;
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
  applicationType: 'chassis' | 'individual';
  period: number;
  status: string;
  createdAt: any;
}

interface LotteryResult {
  applicationId: string;
  spaceId: string;
  spaceNumber: string;
  applicantName: string;
  isReserve: boolean;
}

interface ScheduledParkingInfo {
  applicationId: string;
  spaceId: string;
  spaceNumber: string;
  applicantInfo: {
    name: string;
    phoneNumber: string;
    organization: string;
  };
  usagePeriod: {
    startDate: Date;
    endDate: Date;
  };
  scheduledStartDate: Date; // 실제 사용 시작일 (다음 달 1일)
}

interface ParkingPeriod {
  id: string;
  sequence: number;
  applicationStartDate: any;
  applicationEndDate: any;
  lotteryDate: any;
  usageStartDate: any;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

interface ParkingPeriodForm {
  sequence: number;
  applicationStartDate: string;
  applicationEndDate: string;
  lotteryDate: string;
  usageStartDate: string;
  isActive: boolean;
}

const ParkingLotteryPage: React.FC = () => {
  const [vacantSpaces, setVacantSpaces] = useState<ParkingSpace[]>([]);
  const [pendingApplications, setPendingApplications] = useState<ParkingApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [lotteryInProgress, setLotteryInProgress] = useState(false);
  const [lotteryResults, setLotteryResults] = useState<LotteryResult[]>([]);
  const [reserveCount, setReserveCount] = useState(0);
  const [showLotteryModal, setShowLotteryModal] = useState(false);
  
  // 신청 차수 관련 상태
  const [periods, setPeriods] = useState<ParkingPeriod[]>([]);
  const [activePeriod, setActivePeriod] = useState<ParkingPeriod | null>(null);
  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [showPeriodHistory, setShowPeriodHistory] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<ParkingPeriod | null>(null);
  const [periodFormData, setPeriodFormData] = useState<ParkingPeriodForm>({
    sequence: 1,
    applicationStartDate: '',
    applicationEndDate: '',
    lotteryDate: '',
    usageStartDate: '',
    isActive: false
  });
  const [periodSubmitting, setPeriodSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
    fetchPeriods();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // 모든 주차면 조회 (vacant 또는 occupied)
      const spacesSnapshot = await getDocs(collection(db, 'parking_spaces'));
      const allSpaces = spacesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ParkingSpace[];
      
      // 추첨 대상 주차면 필터링
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      
      const eligibleSpaces = allSpaces.filter(space => {
        // 1. vacant 상태인 경우
        if (space.status === 'vacant') {
          return true;
        }
        
        // 2. occupied 상태이면서 이번 달에 만료되는 경우
        if (space.status === 'occupied' && space.usagePeriod?.endDate) {
          const endDate = space.usagePeriod.endDate.toDate ? 
            space.usagePeriod.endDate.toDate() : 
            new Date(space.usagePeriod.endDate);
          
          return endDate.getMonth() === currentMonth && endDate.getFullYear() === currentYear;
        }
        
        return false;
      });
      
      // 대기중인 신청 조회
      const applicationsQuery = query(
        collection(db, 'parking_applications'),
        where('status', '==', 'pending')
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      const applications = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ParkingApplication[];

      setVacantSpaces(eligibleSpaces);
      setPendingApplications(applications);
    } catch (error) {
      console.error('데이터 조회 중 오류:', error);
      alert('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriods = async () => {
    try {
      const q = query(
        collection(db, 'parking_periods'),
        orderBy('sequence', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const periodsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ParkingPeriod[];
      
      setPeriods(periodsData);
      
      // 활성화된 차수 찾기
      const currentActivePeriod = periodsData.find(period => period.isActive);
      setActivePeriod(currentActivePeriod || null);
    } catch (error) {
      console.error('신청 차수 목록 조회 중 오류:', error);
    }
  };

  const handleLotteryStart = () => {
    if (pendingApplications.length === 0) {
      alert('추첨 대상 신청이 없습니다.');
      return;
    }
    
    if (vacantSpaces.length === 0) {
      alert('배정 가능한 주차면이 없습니다.');
      return;
    }

    setShowLotteryModal(true);
  };

  const executeLottery = async () => {
    try {
      setLotteryInProgress(true);
      setShowLotteryModal(false);

      // 신청자 목록 섞기
      const shuffledApplications = [...pendingApplications].sort(() => Math.random() - 0.5);
      
      // 주차면 목록 섞기
      const shuffledSpaces = [...vacantSpaces].sort(() => Math.random() - 0.5);
      
      const results: LotteryResult[] = [];
      const availableSpaces = Math.min(shuffledApplications.length, shuffledSpaces.length);
      
      // 정규 합격자 배정
      for (let i = 0; i < availableSpaces; i++) {
        const application = shuffledApplications[i];
        const space = shuffledSpaces[i];
        
        results.push({
          applicationId: application.id,
          spaceId: space.id,
          spaceNumber: space.spaceNumber,
          applicantName: application.name,
          isReserve: false
        });
      }
      
      // 예비 합격자 지정
      const remainingApplications = shuffledApplications.slice(availableSpaces);
      const reserveApplicants = remainingApplications.slice(0, reserveCount);
      
      for (const application of reserveApplicants) {
        results.push({
          applicationId: application.id,
          spaceId: '',
          spaceNumber: '예비',
          applicantName: application.name,
          isReserve: true
        });
      }

      // 추첨 결과 저장 및 상태 업데이트
      await saveLotteryResults(results);
      
      setLotteryResults(results);
      await fetchData(); // 데이터 새로고침
      
    } catch (error) {
      console.error('추첨 실행 중 오류:', error);
      alert('추첨 실행 중 오류가 발생했습니다.');
    } finally {
      setLotteryInProgress(false);
    }
  };

  const saveLotteryResults = async (results: LotteryResult[]) => {
    const batch = [];
    
    for (const result of results) {
      // 추첨 히스토리 저장
      const lotteryHistoryData = {
        applicationId: result.applicationId,
        spaceId: result.spaceId,
        spaceNumber: result.spaceNumber,
        applicantName: result.applicantName,
        isReserve: result.isReserve,
        lotteryDate: serverTimestamp(),
        status: result.isReserve ? 'reserve' : 'selected'
      };
      
      batch.push(addDoc(collection(db, 'parking_lottery_history'), lotteryHistoryData));
      
      // 신청 상태 업데이트
      const newStatus = result.isReserve ? 'reserve' : 'selected';
      batch.push(updateDoc(doc(db, 'parking_applications', result.applicationId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      }));
      
      // 정규 합격자의 예약 정보 저장 (실제 주차면은 익월 1일에 업데이트)
      if (!result.isReserve && result.spaceId) {
        const application = pendingApplications.find(app => app.id === result.applicationId);
        if (application) {
          const nextMonthStart = getNextMonthFirstDay();
          const usageEndDate = new Date(nextMonthStart);
          usageEndDate.setMonth(usageEndDate.getMonth() + application.period);
          
          // 예약 정보를 별도 컬렉션에 저장
          const scheduledParkingData = {
            applicationId: result.applicationId,
            spaceId: result.spaceId,
            spaceNumber: result.spaceNumber,
            applicantInfo: {
              name: application.name,
              phoneNumber: application.phoneNumber,
              organization: application.organization
            },
            usagePeriod: {
              startDate: serverTimestamp(), // 실제로는 nextMonthStart를 사용할 예정
              endDate: serverTimestamp()    // 실제로는 usageEndDate를 사용할 예정
            },
            scheduledStartDate: nextMonthStart,
            scheduledEndDate: usageEndDate,
            status: 'scheduled', // 예약됨 상태
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };
          
          batch.push(addDoc(collection(db, 'parking_scheduled'), scheduledParkingData));
          
          // 주차면에는 예약 상태만 표시 (기존 정보는 유지)
          const spaceUpdate: any = {
            scheduledUser: {
              name: application.name,
              phoneNumber: application.phoneNumber,
              organization: application.organization
            },
            scheduledPeriod: {
              startDate: nextMonthStart,
              endDate: usageEndDate
            },
            hasScheduled: true,
            updatedAt: serverTimestamp()
          };
          
          // 현재 공실인 경우에만 상태를 'scheduled'로 변경
          const currentSpace = vacantSpaces.find(space => space.id === result.spaceId);
          if (currentSpace?.status === 'vacant') {
            spaceUpdate.status = 'scheduled';
          }
          
          batch.push(updateDoc(doc(db, 'parking_spaces', result.spaceId), spaceUpdate));
        }
      }
    }
    
    await Promise.all(batch);
  };

  const getApplicationTypeText = (type: string) => {
    return type === 'chassis' ? '섀시업체' : '개별화물차';
  };

  const getSpaceStatusText = (space: ParkingSpace) => {
    if (space.status === 'vacant') {
      return { text: '공실', color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-300' };
    } else if (space.status === 'scheduled') {
      return { text: '예약됨', color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-300' };
    } else if (space.status === 'occupied' && space.usagePeriod?.endDate) {
      // 예약이 있는 만료예정 주차면
      if ((space as any).hasScheduled) {
        return { text: '만료예정(예약있음)', color: 'text-purple-600', bgColor: 'bg-purple-50', borderColor: 'border-purple-300' };
      }
      return { text: '만료예정', color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-300' };
    }
    return { text: '공실', color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-300' };
  };

  const getNextMonthFirstDay = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return nextMonth;
  };

  // 신청 차수 관련 함수들
  const handlePeriodSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!periodFormData.applicationStartDate || !periodFormData.applicationEndDate || 
        !periodFormData.lotteryDate || !periodFormData.usageStartDate) {
      alert('모든 날짜를 입력해 주세요.');
      return;
    }

    // 날짜 유효성 검사
    const appStart = new Date(periodFormData.applicationStartDate);
    const appEnd = new Date(periodFormData.applicationEndDate);
    const lottery = new Date(periodFormData.lotteryDate);
    const usageStart = new Date(periodFormData.usageStartDate);

    if (appStart >= appEnd) {
      alert('신청 종료일은 신청 시작일보다 늦어야 합니다.');
      return;
    }

    if (lottery <= appEnd) {
      alert('추첨일은 신청 종료일보다 늦어야 합니다.');
      return;
    }

    if (usageStart <= lottery) {
      alert('이용 시작일은 추첨일보다 늦어야 합니다.');
      return;
    }

    setPeriodSubmitting(true);

    try {
      const periodData = {
        sequence: periodFormData.sequence,
        applicationStartDate: new Date(periodFormData.applicationStartDate),
        applicationEndDate: new Date(periodFormData.applicationEndDate),
        lotteryDate: new Date(periodFormData.lotteryDate),
        usageStartDate: new Date(periodFormData.usageStartDate),
        isActive: periodFormData.isActive,
        updatedAt: serverTimestamp()
      };

      if (editingPeriod) {
        // 수정
        await updateDoc(doc(db, 'parking_periods', editingPeriod.id), periodData);
        alert('신청 차수가 수정되었습니다.');
      } else {
        // 신규 추가
        await addDoc(collection(db, 'parking_periods'), {
          ...periodData,
          createdAt: serverTimestamp()
        });
        alert('신청 차수가 등록되었습니다.');
      }

      setShowPeriodForm(false);
      setEditingPeriod(null);
      resetPeriodForm();
      fetchPeriods();
    } catch (error) {
      console.error('신청 차수 처리 중 오류:', error);
      alert('처리 중 오류가 발생했습니다.');
    } finally {
      setPeriodSubmitting(false);
    }
  };

  const resetPeriodForm = () => {
    const nextSequence = periods.length > 0 ? Math.max(...periods.map(p => p.sequence)) + 1 : 1;
    setPeriodFormData({
      sequence: nextSequence,
      applicationStartDate: '',
      applicationEndDate: '',
      lotteryDate: '',
      usageStartDate: '',
      isActive: false
    });
  };

  const handlePeriodEdit = (period: ParkingPeriod) => {
    setEditingPeriod(period);
    setPeriodFormData({
      sequence: period.sequence,
      applicationStartDate: period.applicationStartDate?.toDate?.()?.toISOString().split('T')[0] || '',
      applicationEndDate: period.applicationEndDate?.toDate?.()?.toISOString().split('T')[0] || '',
      lotteryDate: period.lotteryDate?.toDate?.()?.toISOString().split('T')[0] || '',
      usageStartDate: period.usageStartDate?.toDate?.()?.toISOString().split('T')[0] || '',
      isActive: period.isActive
    });
    setShowPeriodForm(true);
  };

  const handlePeriodAdd = () => {
    setEditingPeriod(null);
    resetPeriodForm();
    setShowPeriodForm(true);
  };

  const handlePeriodCancel = () => {
    setShowPeriodForm(false);
    setEditingPeriod(null);
    resetPeriodForm();
  };

  const getPeriodStatusText = (period: ParkingPeriod) => {
    const now = new Date();
    const appStart = period.applicationStartDate?.toDate?.();
    const appEnd = period.applicationEndDate?.toDate?.();
    const lottery = period.lotteryDate?.toDate?.();
    const usageStart = period.usageStartDate?.toDate?.();

    if (!period.isActive) return '비활성화';
    if (now < appStart) return '신청 예정';
    if (now >= appStart && now <= appEnd) return '신청 진행중';
    if (now > appEnd && now < lottery) return '신청 마감';
    if (now >= lottery && now < usageStart) return '추첨 완료';
    if (now >= usageStart) return '이용 중';
    return '알 수 없음';
  };

  const getPeriodStatusColor = (period: ParkingPeriod) => {
    const status = getPeriodStatusText(period);
    switch (status) {
      case '신청 진행중': return 'text-green-600 bg-green-50';
      case '신청 예정': return 'text-blue-600 bg-blue-50';
      case '신청 마감': return 'text-yellow-600 bg-yellow-50';
      case '추첨 완료': return 'text-purple-600 bg-purple-50';
      case '이용 중': return 'text-gray-600 bg-gray-50';
      case '비활성화': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-lg text-gray-600">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-white">
      <div className="w-[1200px] mx-auto relative pt-8">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              주차장 추첨관리
            </div>
            <button
              onClick={handleLotteryStart}
              disabled={lotteryInProgress || pendingApplications.length === 0 || vacantSpaces.length === 0}
              className={`px-6 py-3 rounded-sm font-medium transition-colors ${
                lotteryInProgress || pendingApplications.length === 0 || vacantSpaces.length === 0
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-[#155acb] text-white hover:bg-[#155acb]/90'
              }`}
            >
              {lotteryInProgress ? '추첨 진행 중...' : '추첨 시작'}
            </button>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 주차장 신청 차수 섹션 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[#1e2c49] text-xl font-bold">주차장 신청 차수</h2>
              <div className="flex gap-3">
                <button
                  onClick={handlePeriodAdd}
                  className="px-4 py-2 bg-[#155acb] text-white rounded hover:bg-[#155acb]/90 text-sm"
                >
                  신청 차수 추가
                </button>
                <button
                  onClick={() => setShowPeriodHistory(true)}
                  className="px-4 py-2 border border-[#155acb] text-[#155acb] rounded hover:bg-[#155acb]/10 text-sm"
                >
                  히스토리
                </button>
              </div>
            </div>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
            
            {activePeriod ? (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-[#1e2c49] mb-2">
                      {activePeriod.sequence}차 주차장 신청
                    </h3>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getPeriodStatusColor(activePeriod)}`}>
                      {getPeriodStatusText(activePeriod)}
                    </span>
                  </div>
                  <button
                    onClick={() => handlePeriodEdit(activePeriod)}
                    className="text-[#155acb] hover:text-[#155acb]/80 text-sm font-medium"
                  >
                    수정
                  </button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">신청 시작일</p>
                    <p className="font-medium text-[#1e2c49]">
                      {activePeriod.applicationStartDate?.toDate?.()?.toLocaleDateString('ko-KR') || '미설정'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">신청 종료일</p>
                    <p className="font-medium text-[#1e2c49]">
                      {activePeriod.applicationEndDate?.toDate?.()?.toLocaleDateString('ko-KR') || '미설정'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">추첨일</p>
                    <p className="font-medium text-[#1e2c49]">
                      {activePeriod.lotteryDate?.toDate?.()?.toLocaleDateString('ko-KR') || '미설정'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">이용 시작일</p>
                    <p className="font-medium text-[#1e2c49]">
                      {activePeriod.usageStartDate?.toDate?.()?.toLocaleDateString('ko-KR') || '미설정'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-[#6b7684] bg-gray-50 rounded-lg">
                <p className="mb-4">활성화된 신청 차수가 없습니다.</p>
                <button
                  onClick={handlePeriodAdd}
                  className="px-4 py-2 bg-[#155acb] text-white rounded hover:bg-[#155acb]/90"
                >
                  첫 번째 신청 차수 추가
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 추첨 현황 */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-sm outline outline-1 outline-slate-200 p-6">
            <h3 className="text-[#1e2c49] text-xl font-bold mb-4">추첨 대상 주차면</h3>
            <div className="text-3xl font-bold text-green-600 mb-2">{vacantSpaces.length}면</div>
            <p className="text-[#6b7684]">공실 + 만료예정 주차면</p>
          </div>
          
          <div className="bg-white rounded-sm outline outline-1 outline-slate-200 p-6">
            <h3 className="text-[#1e2c49] text-xl font-bold mb-4">대기 신청</h3>
            <div className="text-3xl font-bold text-blue-600 mb-2">{pendingApplications.length}건</div>
            <p className="text-[#6b7684]">추첨 대상 신청</p>
          </div>
          
          <div className="bg-white rounded-sm outline outline-1 outline-slate-200 p-6">
            <h3 className="text-[#1e2c49] text-xl font-bold mb-4">추첨 비율</h3>
            <div className="text-3xl font-bold text-orange-600 mb-2">
              {pendingApplications.length > 0 ? 
                `${Math.min(vacantSpaces.length, pendingApplications.length)} : ${pendingApplications.length}` : 
                '0 : 0'
              }
            </div>
            <p className="text-[#6b7684]">배정 : 신청</p>
          </div>
        </div>

                  {/* 추첨 결과 */}
          {lotteryResults.length > 0 && (
            <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-8">
                <h2 className="text-[#1e2c49] text-xl font-bold mb-4">추첨 결과</h2>
                <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
                
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-700">
                    <strong>사용 시작일:</strong> {getNextMonthFirstDay().toLocaleDateString()} (익월 1일부터 사용 가능)
                  </p>
                </div>
                
                <div className="space-y-4">
                  {lotteryResults.map((result, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-sm border-2 ${
                        result.isReserve ? 'border-yellow-300 bg-yellow-50' : 'border-green-300 bg-green-50'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-semibold text-[#1e2c49]">{result.applicantName}</span>
                          <span className={`ml-3 px-2 py-1 rounded text-sm ${
                            result.isReserve ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'
                          }`}>
                            {result.isReserve ? '예비합격' : '정규합격'}
                          </span>
                        </div>
                        <div className="text-[#6b7684]">
                          {result.isReserve ? '예비 대기' : `${result.spaceNumber}번 주차면`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        {/* 대기 신청 목록 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold mb-4">대기 신청 목록</h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
            
            {pendingApplications.length === 0 ? (
              <div className="text-center py-8 text-[#6b7684]">
                대기중인 신청이 없습니다.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#e5e8eb]">
                      <th className="text-left py-3 text-[#6b7684] font-medium">신청자</th>
                      <th className="text-left py-3 text-[#6b7684] font-medium">소속</th>
                      <th className="text-left py-3 text-[#6b7684] font-medium">신청유형</th>
                      <th className="text-left py-3 text-[#6b7684] font-medium">기간</th>
                      <th className="text-left py-3 text-[#6b7684] font-medium">신청일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingApplications.map((application) => (
                      <tr key={application.id} className="border-b border-[#e5e8eb]">
                        <td className="py-4 text-[#1e2c49] font-semibold">{application.name}</td>
                        <td className="py-4 text-[#6b7684]">{application.organization}</td>
                        <td className="py-4 text-[#6b7684]">{getApplicationTypeText(application.applicationType)}</td>
                        <td className="py-4 text-[#6b7684]">{application.period}개월</td>
                        <td className="py-4 text-[#6b7684]">
                          {application.createdAt?.toDate?.()?.toLocaleDateString() || '알 수 없음'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* 추첨 대상 주차면 목록 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200 mb-8">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold mb-4">추첨 대상 주차면 목록</h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
            
            {vacantSpaces.length === 0 ? (
              <div className="text-center py-8 text-[#6b7684]">
                추첨 대상 주차면이 없습니다.
              </div>
            ) : (
              <div className="grid grid-cols-6 gap-4">
                {vacantSpaces.map((space) => {
                  const statusInfo = getSpaceStatusText(space);
                  return (
                    <div 
                      key={space.id}
                      className={`p-4 border ${statusInfo.borderColor} ${statusInfo.bgColor} rounded-sm text-center`}
                    >
                      <div className="font-semibold text-[#1e2c49]">{space.spaceNumber}</div>
                      <div className={`text-sm ${statusInfo.color}`}>{statusInfo.text}</div>
                      {space.status === 'occupied' && space.usagePeriod?.endDate && (
                        <div className="text-xs text-gray-500 mt-1">
                          {space.usagePeriod.endDate.toDate ? 
                            space.usagePeriod.endDate.toDate().toLocaleDateString() : 
                            new Date(space.usagePeriod.endDate).toLocaleDateString()
                          } 만료
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 추첨 설정 모달 */}
      {showLotteryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">추첨 설정</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">추첨 현황</p>
                  <div className="bg-gray-50 p-3 rounded">
                    <p>• 대기 신청: {pendingApplications.length}건</p>
                    <p>• 추첨 대상 주차면: {vacantSpaces.length}면</p>
                    <p>• 정규 합격: {Math.min(pendingApplications.length, vacantSpaces.length)}명</p>
                    <p className="text-sm text-blue-600 mt-2">※ 사용 시작일: {getNextMonthFirstDay().toLocaleDateString()}</p>
                  </div>
                </div>
                
                {pendingApplications.length > vacantSpaces.length && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      예비 합격자 수
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={pendingApplications.length - vacantSpaces.length}
                      value={reserveCount}
                      onChange={(e) => setReserveCount(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      최대 {pendingApplications.length - vacantSpaces.length}명까지 설정 가능
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowLotteryModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={executeLottery}
                  className="px-4 py-2 bg-[#155acb] text-white rounded hover:bg-[#155acb]/90"
                >
                  추첨 실행
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 신청 차수 등록/수정 폼 */}
      {showPeriodForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">
                  {editingPeriod ? '신청 차수 수정' : '신청 차수 추가'}
                </h3>
                <button
                  onClick={handlePeriodCancel}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handlePeriodSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    차수 번호
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={periodFormData.sequence}
                    onChange={(e) => setPeriodFormData(prev => ({ ...prev, sequence: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      신청 시작일
                    </label>
                    <input
                      type="date"
                      value={periodFormData.applicationStartDate}
                      onChange={(e) => setPeriodFormData(prev => ({ ...prev, applicationStartDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      신청 종료일
                    </label>
                    <input
                      type="date"
                      value={periodFormData.applicationEndDate}
                      onChange={(e) => setPeriodFormData(prev => ({ ...prev, applicationEndDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      추첨일
                    </label>
                    <input
                      type="date"
                      value={periodFormData.lotteryDate}
                      onChange={(e) => setPeriodFormData(prev => ({ ...prev, lotteryDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      이용 시작일
                    </label>
                    <input
                      type="date"
                      value={periodFormData.usageStartDate}
                      onChange={(e) => setPeriodFormData(prev => ({ ...prev, usageStartDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#155acb]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={periodFormData.isActive}
                      onChange={(e) => setPeriodFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="h-4 w-4 text-[#155acb] focus:ring-[#155acb] border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">활성화</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    활성화된 차수만 사용자에게 표시됩니다.
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handlePeriodCancel}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={periodSubmitting}
                    className={`px-4 py-2 rounded text-white ${
                      periodSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#155acb] hover:bg-[#155acb]/90'
                    }`}
                  >
                    {periodSubmitting ? '처리 중...' : (editingPeriod ? '수정' : '등록')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 신청 차수 히스토리 */}
      {showPeriodHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">신청 차수 히스토리</h3>
                <button
                  onClick={() => setShowPeriodHistory(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {periods.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  등록된 신청 차수가 없습니다.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          차수
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          신청 기간
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          추첨일
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          이용 시작일
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          상태
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {periods.map((period) => (
                        <tr key={period.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {period.sequence}차
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {period.applicationStartDate?.toDate?.()?.toLocaleDateString('ko-KR')} ~ {period.applicationEndDate?.toDate?.()?.toLocaleDateString('ko-KR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {period.lotteryDate?.toDate?.()?.toLocaleDateString('ko-KR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {period.usageStartDate?.toDate?.()?.toLocaleDateString('ko-KR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPeriodStatusColor(period)}`}>
                              {getPeriodStatusText(period)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => {
                                setShowPeriodHistory(false);
                                handlePeriodEdit(period);
                              }}
                              className="text-[#155acb] hover:text-[#155acb]/80"
                            >
                              수정
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParkingLotteryPage; 